import { Injectable } from '@angular/core';
import { setProps } from '@ngneat/elf';
import { Observable } from 'rxjs';

import { appStore } from './app.store';
import { appSelector } from './app.selectors';

@Injectable({ providedIn: 'root' })
export class AppRepository {
	public backgroundLoading$ = appSelector.backgroundLoading$
	public overlayLoading$ = appSelector.overlayLoading$;

	public withBackgroundLoading<T>(thresholdMs = 200) {
		return this.withLoading<T>(() => this.incrementBackgroundLoading(), () => this.decrementBackgroundLoading(), thresholdMs);
	}

	public withOverlayLoading<T>() {
		return this.withLoading<T>(() => this.incrementLoading(), () => this.decrementLoading(), 0);
	}

	private withLoading<T>(increment: () => void, decrement: () => void, thresholdMs: number) {
		return (source: Observable<T>): Observable<T> => {
			return new Observable<T>(subscriber => {
				// Defer the counter mutation so it doesn't land inside the change-detection
				// cycle that triggered the subscription (would cause NG0100), and so fast
				// requests that finish before the threshold never flip the loading state —
				// avoiding flicker on disabled/loading-styled UI bound to the counter.
				let phase: 'pending' | 'incremented' | 'closed' = 'pending';
				const timer = setTimeout(() => {
					if (phase !== 'pending') return;
					phase = 'incremented';
					increment();
				}, thresholdMs);
				const finish = () => {
					if (phase === 'incremented') {
						decrement();
					}
					if (phase === 'pending') {
						clearTimeout(timer);
					}
					phase = 'closed';
				};
				const sub = source.subscribe({
					next: (val) => subscriber.next(val),
					error: (err) => {
						finish();
						subscriber.error(err);
					},
					complete: () => {
						finish();
						subscriber.complete();
					}
				});
				return () => {
					finish();
					sub.unsubscribe();
				};
			});
		};
	}

	public incrementBackgroundLoading(): void {
		const current = appStore.getValue().backgroundLoadingCounter;
		appStore.update(setProps({ backgroundLoadingCounter: current + 1 }));
	}

	public decrementBackgroundLoading(): void {
		const current = appStore.getValue().backgroundLoadingCounter;
		appStore.update(setProps({ backgroundLoadingCounter: Math.max(0, current - 1) }));
	}

	public incrementLoading(): void {
		const current = appStore.getValue().loadingCounter;
		appStore.update(setProps({ loadingCounter: current + 1 }));
	}

	public decrementLoading(): void {
		const current = appStore.getValue().loadingCounter;
		const next = Math.max(0, current - 1);
		appStore.update(setProps({ loadingCounter: next }));
	}

	public queryBackgroundLoading(): boolean {
		return appStore.query(
			(state) => state.backgroundLoadingCounter > 0
		);
	}
}
